import type { ImageMeta, Point, User } from './types'

export class HttpError extends Error {
  status: number
  body: any
  constructor(status: number, message: string, body?: any) {
    super(message)
    this.status = status
    this.body = body
  }
}

async function http<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(path, {
    ...init,
    headers: { 'content-type': 'application/json', ...(init?.headers || {}) },
  })
  if (!r.ok) {
    let body: any = null
    let msg = r.statusText
    try {
      body = await r.json()
      if (body?.error) msg = body.error
    } catch {
      try {
        msg = await r.text()
      } catch {}
    }
    throw new HttpError(r.status, msg, body)
  }
  return (await r.json()) as T
}

export async function uploadImage(file: File, user: User): Promise<ImageMeta> {
  const fd = new FormData()
  fd.append('file', file)
  fd.append('user', JSON.stringify(user))
  const r = await fetch('/api/upload', { method: 'POST', body: fd })
  if (!r.ok) throw new Error(`upload failed: ${r.status} ${await r.text()}`)
  return (await r.json()) as ImageMeta
}

export async function listGallery(): Promise<ImageMeta[]> {
  const { images } = await http<{ images: ImageMeta[] }>('/api/gallery')
  return images
}

export async function listPoints(imageId: string): Promise<Point[]> {
  const { points } = await http<{ points: Point[] }>(`/api/points/${imageId}`)
  return points
}

export async function generatePoint(args: {
  imageId: string
  user: User
  xRatio: number
  yRatio: number
}): Promise<Point> {
  const { point } = await http<{ point: Point }>(`/api/generate`, {
    method: 'POST',
    body: JSON.stringify(args),
  })
  return point
}

export async function regeneratePoint(pointId: string, user: User): Promise<Point> {
  const { point } = await http<{ point: Point }>(`/api/regenerate`, {
    method: 'POST',
    body: JSON.stringify({ pointId, user }),
  })
  return point
}

export interface LightboxGenResult {
  point: Point | null
  dramaUrl: string
  r2Key: string
  sceneZh: string
  targetLabel: string
  direction: 'before' | 'after'
  timeOffset: string
}

export async function lightboxGenerate(
  r2Key: string,
  xRatio: number,
  yRatio: number,
  user: User,
  parentPointId?: string,
): Promise<LightboxGenResult> {
  return await http<LightboxGenResult>('/api/lightbox-gen', {
    method: 'POST',
    body: JSON.stringify({ r2Key, xRatio, yRatio, user, parentPointId }),
  })
}

export async function deleteImage(imageId: string, user: User): Promise<void> {
  await http<{ ok: boolean }>(`/api/image/${encodeURIComponent(imageId)}/delete`, {
    method: 'POST',
    body: JSON.stringify({ user }),
  })
}
